import * as THREE from 'three'
import { ClipPolygonMaxVerticeCount, ClipTask } from '@/shared/constants'
import type { ClipVolume } from '@/shared/types/core'
import type { ViewId } from '@/shared/types/view'

/** 同时参与共享裁切判定的裁切体总数。 */
export const MAX_COMPILED_CLIP_VOLUMES = 8

/** volume group 数量上限。 */
export const MAX_COMPILED_CLIP_GROUPS = 8

/** CPU/GLSL 边界判定使用的统一容差。 */
export const COMPILED_CLIP_EPSILON = 1e-5

/** 统一裁切预览色。 */
export const COMPILED_CLIP_HIGHLIGHT_COLOR = new THREE.Color(0.85, 0.07, 0.36)

/** GPU packet metadata 中记录的裁切体类型。 */
export enum CompiledClipVolumeKind {
  POLYGON = 0,
  BOX = 1,
}

interface CompiledClipVolumeBase {
  readonly kind: CompiledClipVolumeKind
  readonly clipTask: ClipTask
  readonly groupIndex: number
}

/** 单个屏幕空间裁切多边形的只读编译结果。 */
export interface CompiledClipPolygon extends CompiledClipVolumeBase {
  readonly kind: CompiledClipVolumeKind.POLYGON
  readonly worldViewProjection: THREE.Matrix4
  readonly vertices: ReadonlyArray<THREE.Vector2>
  readonly aabb: THREE.Vector4
}

/** 单个世界空间 Box 的只读编译结果。 */
export interface CompiledClipBox extends CompiledClipVolumeBase {
  readonly kind: CompiledClipVolumeKind.BOX
  /** 将世界坐标转换到单位盒 [-0.5, 0.5]^3 的矩阵。 */
  readonly worldToBox: THREE.Matrix4
}

export type CompiledClipVolume = CompiledClipPolygon | CompiledClipBox

/** 资产无关的裁切状态快照。 */
export interface CompiledClipState {
  readonly version: number
  readonly volumes: ReadonlyArray<CompiledClipVolume>
  readonly polygons: ReadonlyArray<CompiledClipPolygon>
  readonly boxes: ReadonlyArray<CompiledClipBox>
  readonly droppedCount?: number
}

/** 单个世界点经过裁切规则计算后的结果。 */
export interface CompiledClipEvaluation {
  readonly hidden: boolean
  readonly highlighted: boolean
}

let nextCompiledClipStateVersion = 1

function isFiniteMatrix(matrix: THREE.Matrix4 | undefined): matrix is THREE.Matrix4 {
  return !!matrix && matrix.elements.every(Number.isFinite)
}

function isAffineMatrix(matrix: THREE.Matrix4): boolean {
  const elements = matrix.elements
  return (
    Math.abs(elements[3]!) <= COMPILED_CLIP_EPSILON &&
    Math.abs(elements[7]!) <= COMPILED_CLIP_EPSILON &&
    Math.abs(elements[11]!) <= COMPILED_CLIP_EPSILON &&
    Math.abs(elements[15]! - 1) <= COMPILED_CLIP_EPSILON
  )
}

function isFiniteVertex(vertex: THREE.Vector3): boolean {
  return (
    Number.isFinite(Math.fround(vertex.x)) &&
    Number.isFinite(Math.fround(vertex.y)) &&
    Number.isFinite(Math.fround(vertex.z))
  )
}

function areVerticesNear(left: { x: number; y: number }, right: { x: number; y: number }): boolean {
  const deltaX = left.x - right.x
  const deltaY = left.y - right.y
  return deltaX * deltaX + deltaY * deltaY <= COMPILED_CLIP_EPSILON * COMPILED_CLIP_EPSILON
}

function normalizeClipVertices(vertices: ReadonlyArray<THREE.Vector3>): THREE.Vector2[] {
  const normalized: THREE.Vector2[] = []

  for (const vertex of vertices) {
    const current = new THREE.Vector2(vertex.x, vertex.y)
    const previous = normalized[normalized.length - 1]
    if (!previous || !areVerticesNear(previous, current)) {
      normalized.push(current)
    }
  }

  if (
    normalized.length > 1 &&
    areVerticesNear(normalized[0]!, normalized[normalized.length - 1]!)
  ) {
    normalized.pop()
  }

  return normalized
}

function resolveClipTask(value: number | undefined): ClipTask | null {
  if (
    value === ClipTask.HIGHLIGHT ||
    value === ClipTask.SHOW_INSIDE ||
    value === ClipTask.SHOW_OUTSIDE
  ) {
    return value
  }
  return null
}

function computeAabb(vertices: ReadonlyArray<THREE.Vector2>): THREE.Vector4 {
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  for (const vertex of vertices) {
    minX = Math.min(minX, vertex.x)
    minY = Math.min(minY, vertex.y)
    maxX = Math.max(maxX, vertex.x)
    maxY = Math.max(maxY, vertex.y)
  }

  return new THREE.Vector4(minX, minY, maxX, maxY)
}

function freezeMatrix(matrix: THREE.Matrix4): THREE.Matrix4 {
  Object.freeze(matrix.elements)
  Object.freeze(matrix)
  return matrix
}

function freezeVector2(vector: THREE.Vector2): THREE.Vector2 {
  Object.freeze(vector)
  return vector
}

function freezeVector4(vector: THREE.Vector4): THREE.Vector4 {
  Object.freeze(vector)
  return vector
}

function hasPolygonArea(vertices: ReadonlyArray<THREE.Vector2>): boolean {
  let twiceArea = 0
  let previous = vertices[vertices.length - 1]!
  for (const current of vertices) {
    twiceArea += previous.x * current.y - current.x * previous.y
    previous = current
  }
  return Math.abs(twiceArea) > COMPILED_CLIP_EPSILON * COMPILED_CLIP_EPSILON
}

function compilePolygon(
  volume: ClipVolume,
  clipTask: ClipTask,
  groupIndex: number
): CompiledClipPolygon | null {
  const vertices = volume.vertices
  if (
    volume.space !== 'screen' ||
    !vertices ||
    vertices.length < 3 ||
    vertices.length > ClipPolygonMaxVerticeCount ||
    !vertices.every(isFiniteVertex) ||
    !isFiniteMatrix(volume.viewMatrix) ||
    !isFiniteMatrix(volume.projMatrix)
  ) {
    return null
  }

  const clipVertices = normalizeClipVertices(vertices)
  if (clipVertices.length < 3 || !hasPolygonArea(clipVertices)) {
    return null
  }

  const worldViewProjection = new THREE.Matrix4().multiplyMatrices(
    volume.projMatrix,
    volume.viewMatrix
  )
  if (!worldViewProjection.elements.every((value) => Number.isFinite(Math.fround(value)))) {
    return null
  }
  freezeMatrix(worldViewProjection)
  for (const vertex of clipVertices) freezeVector2(vertex)
  Object.freeze(clipVertices)

  return Object.freeze({
    kind: CompiledClipVolumeKind.POLYGON,
    worldViewProjection,
    vertices: clipVertices,
    aabb: freezeVector4(computeAabb(clipVertices)),
    clipTask,
    groupIndex,
  })
}

function compileBox(
  volume: ClipVolume,
  clipTask: ClipTask,
  groupIndex: number
): CompiledClipBox | null {
  const determinant = volume.matrix?.determinant() ?? Number.NaN
  if (
    (volume.space != null && volume.space !== 'world') ||
    !isFiniteMatrix(volume.matrix) ||
    !isAffineMatrix(volume.matrix) ||
    !Number.isFinite(determinant) ||
    determinant === 0
  ) {
    return null
  }

  const worldToBox = volume.matrix.clone().invert()
  if (!worldToBox.elements.every((value) => Number.isFinite(Math.fround(value)))) {
    return null
  }

  return Object.freeze({
    kind: CompiledClipVolumeKind.BOX,
    worldToBox: freezeMatrix(worldToBox),
    clipTask,
    groupIndex,
  })
}

function isVolumeInScope(volume: ClipVolume, viewId?: ViewId): boolean {
  return volume.viewId == null || (viewId !== undefined && volume.viewId === viewId)
}

/** 将 ClipService volume 编译为资产无关的冻结裁切快照。 */
export function createCompiledClipState(
  inputVolumes: ReadonlyArray<ClipVolume>,
  viewId?: ViewId
): CompiledClipState {
  const volumes: CompiledClipVolume[] = []
  const groupIndices = new Map<string, number>()
  let droppedCount = 0

  for (const volume of inputVolumes) {
    if (!isVolumeInScope(volume, viewId)) {
      continue
    }
    if (volumes.length >= MAX_COMPILED_CLIP_VOLUMES) {
      droppedCount += 1
      continue
    }

    const clipTask = resolveClipTask(volume.clipTask)
    if (clipTask === null) {
      droppedCount += 1
      continue
    }

    const groupKey = volume.groupId ?? '__default__'
    const existingGroupIndex = groupIndices.get(groupKey)
    const isNewGroup = existingGroupIndex === undefined
    const groupIndex = existingGroupIndex ?? groupIndices.size
    if (isNewGroup && groupIndex >= MAX_COMPILED_CLIP_GROUPS) {
      droppedCount += 1
      continue
    }

    const compiled =
      volume.type === 'polygon'
        ? compilePolygon(volume, clipTask, groupIndex)
        : compileBox(volume, clipTask, groupIndex)
    if (!compiled) {
      droppedCount += 1
      continue
    }

    if (isNewGroup) {
      groupIndices.set(groupKey, groupIndex)
    }
    volumes.push(compiled)
  }

  const frozenVolumes = Object.freeze(volumes)
  return Object.freeze({
    version: nextCompiledClipStateVersion++,
    volumes: frozenVolumes,
    polygons: Object.freeze(
      volumes.filter(
        (volume): volume is CompiledClipPolygon => volume.kind === CompiledClipVolumeKind.POLYGON
      )
    ),
    boxes: Object.freeze(
      volumes.filter(
        (volume): volume is CompiledClipBox => volume.kind === CompiledClipVolumeKind.BOX
      )
    ),
    droppedCount,
  })
}

function areMatricesEqual(left: THREE.Matrix4, right: THREE.Matrix4): boolean {
  return left.elements.every((value, index) => value === right.elements[index])
}

/** 判断两个已编译裁切快照的有效内容是否完全相同。 */
export function areCompiledClipStatesEqual(
  left: CompiledClipState,
  right: CompiledClipState
): boolean {
  if (left.volumes.length !== right.volumes.length) {
    return false
  }

  for (let volumeIndex = 0; volumeIndex < left.volumes.length; volumeIndex += 1) {
    const leftVolume = left.volumes[volumeIndex]!
    const rightVolume = right.volumes[volumeIndex]!
    if (
      leftVolume.kind !== rightVolume.kind ||
      leftVolume.clipTask !== rightVolume.clipTask ||
      leftVolume.groupIndex !== rightVolume.groupIndex
    ) {
      return false
    }

    if (
      leftVolume.kind === CompiledClipVolumeKind.BOX &&
      rightVolume.kind === CompiledClipVolumeKind.BOX
    ) {
      if (!areMatricesEqual(leftVolume.worldToBox, rightVolume.worldToBox)) {
        return false
      }
      continue
    }

    if (
      leftVolume.kind !== CompiledClipVolumeKind.POLYGON ||
      rightVolume.kind !== CompiledClipVolumeKind.POLYGON ||
      leftVolume.vertices.length !== rightVolume.vertices.length ||
      !leftVolume.aabb.equals(rightVolume.aabb) ||
      !areMatricesEqual(leftVolume.worldViewProjection, rightVolume.worldViewProjection) ||
      leftVolume.vertices.some(
        (vertex, vertexIndex) => !vertex.equals(rightVolume.vertices[vertexIndex]!)
      )
    ) {
      return false
    }
  }

  return true
}

function projectWorldPoint(
  point: THREE.Vector3,
  matrix: THREE.Matrix4,
  target: THREE.Vector2
): boolean {
  const elements = matrix.elements
  const x = elements[0] * point.x + elements[4] * point.y + elements[8] * point.z + elements[12]
  const y = elements[1] * point.x + elements[5] * point.y + elements[9] * point.z + elements[13]
  const w = elements[3] * point.x + elements[7] * point.y + elements[11] * point.z + elements[15]

  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(w) ||
    w <= COMPILED_CLIP_EPSILON
  ) {
    return false
  }

  target.set(x / w, y / w)
  return Number.isFinite(target.x) && Number.isFinite(target.y)
}

function pointOnSegment(point: THREE.Vector2, start: THREE.Vector2, end: THREE.Vector2): boolean {
  const edgeX = end.x - start.x
  const edgeY = end.y - start.y
  const edgeLengthSq = edgeX * edgeX + edgeY * edgeY
  const pointX = point.x - start.x
  const pointY = point.y - start.y
  if (edgeLengthSq <= COMPILED_CLIP_EPSILON * COMPILED_CLIP_EPSILON) {
    return pointX * pointX + pointY * pointY <= COMPILED_CLIP_EPSILON * COMPILED_CLIP_EPSILON
  }

  const cross = edgeX * pointY - edgeY * pointX
  const edgeLength = Math.sqrt(edgeLengthSq)
  if (Math.abs(cross) > COMPILED_CLIP_EPSILON * Math.max(1, edgeLength)) {
    return false
  }

  const dot = pointX * edgeX + pointY * edgeY
  return dot >= -COMPILED_CLIP_EPSILON && dot <= edgeLengthSq + COMPILED_CLIP_EPSILON
}

/** 判断 NDC 点是否位于指定裁切多边形内部或边界容差内。 */
export function isPointInCompiledClipPolygon(
  point: THREE.Vector2,
  polygon: CompiledClipPolygon
): boolean {
  const aabb = polygon.aabb
  if (
    point.x < aabb.x - COMPILED_CLIP_EPSILON ||
    point.y < aabb.y - COMPILED_CLIP_EPSILON ||
    point.x > aabb.z + COMPILED_CLIP_EPSILON ||
    point.y > aabb.w + COMPILED_CLIP_EPSILON
  ) {
    return false
  }

  let inside = false
  let previous = polygon.vertices[polygon.vertices.length - 1]!
  for (const current of polygon.vertices) {
    if (pointOnSegment(point, previous, current)) {
      return true
    }

    const crossesY = current.y > point.y !== previous.y > point.y
    if (crossesY) {
      const intersectionX =
        ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y) + current.x
      if (point.x < intersectionX) {
        inside = !inside
      }
    }
    previous = current
  }

  return inside
}

/** 判断世界坐标点是否位于指定 Box 内部或边界容差内。 */
export function isPointInCompiledClipBox(point: THREE.Vector3, box: CompiledClipBox): boolean {
  const boxPoint = point.clone().applyMatrix4(box.worldToBox)
  return (
    Number.isFinite(boxPoint.x) &&
    Number.isFinite(boxPoint.y) &&
    Number.isFinite(boxPoint.z) &&
    boxPoint.x >= -0.5 - COMPILED_CLIP_EPSILON &&
    boxPoint.x <= 0.5 + COMPILED_CLIP_EPSILON &&
    boxPoint.y >= -0.5 - COMPILED_CLIP_EPSILON &&
    boxPoint.y <= 0.5 + COMPILED_CLIP_EPSILON &&
    boxPoint.z >= -0.5 - COMPILED_CLIP_EPSILON &&
    boxPoint.z <= 0.5 + COMPILED_CLIP_EPSILON
  )
}

/** 判断世界坐标点是否命中单个已编译裁切体。 */
export function isPointInCompiledClipVolume(
  point: THREE.Vector3,
  volume: CompiledClipVolume
): boolean {
  if (volume.kind === CompiledClipVolumeKind.BOX) {
    return isPointInCompiledClipBox(point, volume)
  }
  const projectedPoint = new THREE.Vector2()
  return (
    projectWorldPoint(point, volume.worldViewProjection, projectedPoint) &&
    isPointInCompiledClipPolygon(projectedPoint, volume)
  )
}

/** 按统一 group/task 语义计算世界坐标点的隐藏与高亮结果。 */
export function evaluateCompiledClipState(
  state: CompiledClipState,
  worldPoint: THREE.Vector3
): CompiledClipEvaluation {
  if (state.volumes.length === 0) {
    return { hidden: false, highlighted: false }
  }

  let insideGroupMask = 0
  let matchedInsideGroupMask = 0
  let highlighted = false

  for (const volume of state.volumes) {
    const inside = isPointInCompiledClipVolume(worldPoint, volume)
    if (volume.clipTask === ClipTask.SHOW_OUTSIDE && inside) {
      return { hidden: true, highlighted: false }
    }

    if (volume.clipTask === ClipTask.SHOW_INSIDE) {
      const groupBit = 1 << volume.groupIndex
      insideGroupMask |= groupBit
      if (inside) {
        matchedInsideGroupMask |= groupBit
      }
    } else if (volume.clipTask === ClipTask.HIGHLIGHT && inside) {
      highlighted = true
    }
  }

  if ((insideGroupMask & matchedInsideGroupMask) !== insideGroupMask) {
    return { hidden: true, highlighted: false }
  }

  return { hidden: false, highlighted }
}
